import { FinalTournamentView } from '../components/scoring/FinalTournamentView';
import { ScorerWaitLists } from '../components/scoring/ScorerWaitLists';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { round2GenderLabel } from '../components/scoring/Round2SetupCard';
import { ApiError, apiFetch } from '../lib/api';
import { useScoringTournament } from '../hooks/useScoringTournament';
import { scoringListPath, scoringRound2ShooterPath } from '../lib/scoring-routes';
import { scoringTitle } from '../lib/page-title';
import { useCheckInPoll } from '../hooks/useCheckInPoll';
import { tournamentHasRound2, type Round2AdminView } from '../lib/round2';

const fieldClass =
  'h-12 w-full rounded-[var(--radius-md)] border border-[var(--color-border-strong)] bg-[var(--color-surface-1)] px-3 text-base text-[var(--color-text-primary)]';

/** /admin/scoring/:slug/round2 — qualified roster, teams, and Round 2 team leaderboard. Admins only. */
export function Round2Page() {
  const navigate = useNavigate();
  const { slug: slugParam } = useParams();
  const { slug, detail, notFound, error } = useScoringTournament(slugParam);
  const tournamentId = detail?.tournament.id;
  const lanes = useCheckInPoll(tournamentId ?? null, Boolean(tournamentId), 2);
  const rifle = detail ? tournamentHasRound2(detail.tournament) : false;
  const [view, setView] = useState<Round2AdminView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rename, setRename] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    document.title = scoringTitle('Round 2', detail?.tournament.title);
  }, [detail?.tournament.title]);

  useEffect(() => {
    if (!tournamentId || !rifle) return;
    let cancelled = false;
    apiFetch<{ round2: Round2AdminView }>(`/api/admin/shooting/tournaments/${tournamentId}/round2`)
      .then((body) => {
        if (!cancelled) setView(body.round2);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(err instanceof ApiError ? err.message : 'Could not load Round 2.');
      });
    return () => {
      cancelled = true;
    };
  }, [tournamentId, rifle]);

  async function saveTeamName(teamId: string, name: string) {
    if (!tournamentId) return;
    try {
      const body = await apiFetch<{ round2: Round2AdminView }>(
        `/api/admin/shooting/tournaments/${tournamentId}/round2/teams/${teamId}`,
        { method: 'PATCH', body: JSON.stringify({ name }) },
      );
      setRename(null);
      setView(body.round2);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Could not rename that team.');
    }
  }

  const groups = view ? groupRoster(view) : [];

  if (detail?.tournament.status === 'final') return <FinalTournamentView detail={detail} round2={view} />;

  return (
    <Layout>
      <div className="mx-auto max-w-3xl space-y-5">
        <nav>
          <Link to={scoringListPath(slug)} className="inline-flex min-h-11 items-center text-sm font-medium text-[var(--color-accent)]">
            ← Round 1 scoring
          </Link>
        </nav>
        <header>
          <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">Round 2</h1>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            {detail?.tournament.title ?? 'Rifle'} · {view?.shotsPerShooter ?? 5} shots each, all count. Teams and members qualify from Round 1.
          </p>
        </header>

        {error || loadError ? (
          <p className="rounded-[var(--radius-md)] bg-[var(--color-error-subtle)] px-4 py-3 text-sm text-[var(--color-error-text)]">
            {loadError || error}
          </p>
        ) : null}
        {notFound ? <p className="text-sm text-[var(--color-text-secondary)]">There is no tournament called “{slug}”.</p> : null}
        {detail && !rifle ? <p className="text-sm text-[var(--color-text-secondary)]">Round 2 is only for rifle shooting.</p> : null}

        {view ? (
          <>
            <form key={view.shotsPerShooter} className="flex flex-wrap items-end gap-3" onSubmit={(event) => {
              event.preventDefault();
              const shotsPerShooter = Number(new FormData(event.currentTarget).get('shotsPerShooter'));
              setLoadError(null);
              void apiFetch<{ round2: Round2AdminView }>(`/api/admin/shooting/tournaments/${tournamentId}/round2/settings`, { method: 'PATCH', body: JSON.stringify({ shotsPerShooter }) })
                .then((body) => setView(body.round2))
                .catch((err: unknown) => setLoadError(err instanceof ApiError ? err.message : 'Could not save the Round 2 shot count.'));
            }}>
              <label className="text-sm font-medium">Shots per shooter in Round 2<input className={fieldClass} type="number" name="shotsPerShooter" min="1" max="20" required defaultValue={view.shotsPerShooter} /></label>
              <button className="min-h-11 rounded-[var(--radius-md)] border border-[var(--color-border-strong)] px-4" type="submit">Save</button>
            </form>
            <section aria-label="On the lanes now" className="space-y-2 rounded-[var(--radius-lg)] border border-[var(--color-border)] p-4">
              <h2 className="font-semibold">On the lanes now ({(lanes?.lanes ?? []).filter(lane => lane.shooterId).length})</h2>
              {(lanes?.lanes ?? []).filter((lane) => lane.shooterId).map((lane) => {
                const shooter = view.shooters.find((row) => row.sourceShooterId === lane.shooterId);
                return shooter ? <Link className="block min-h-11 text-[var(--color-accent)]" key={lane.lane} to={scoringRound2ShooterPath(slug, shooter)}>Lane {lane.lane} · {lane.name}</Link> : null;
              })}
            </section>
            <ScorerWaitLists list={lanes?.scorer ?? { round: 2, shotsPlanned: view.shotsPerShooter, waiting: [], complete: [] }} onSelect={row => {
              navigate(scoringRound2ShooterPath(slug, { id: row.shooterId, name: row.name, bib: row.bib }));
            }} />
            {view.warnings.length > 0 ? (
              <ul className="space-y-1 rounded-[var(--radius-md)] border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950">
                {view.warnings.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            ) : null}

            <section className="space-y-2" aria-label="Teams">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Teams</h2>
              </div>
              <ul className="space-y-2">
                {view.teams.map((team) => (
                  <li key={team.id} className="flex items-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-border)] px-3 py-2">
                    {rename?.id === team.id ? (
                      <input
                        autoFocus
                        value={rename.name}
                        aria-label={`Rename ${team.name}`}
                        onChange={(event) => setRename({ id: team.id, name: event.target.value })}
                        onBlur={() => void saveTeamName(team.id, rename.name)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            void saveTeamName(team.id, rename.name);
                          }
                          if (event.key === 'Escape') setRename(null);
                        }}
                        className={fieldClass}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setRename({ id: team.id, name: team.name })}
                        className="min-h-11 flex-1 truncate text-left text-base font-semibold text-[var(--color-text-primary)]"
                      >
                        {team.name}
                      </button>
                    )}
                    <span className="shrink-0 text-sm tabular-nums text-[var(--color-text-tertiary)]">{team.memberCount}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-[var(--color-text-tertiary)]">Qualifying Round 1 teams advance with their full roster. Each shooter starts Round 2 at 0 with {view.shotsPerShooter} new shots. Tap a team name to rename it.</p>
            </section>

            <section aria-label="Round 2 roster" className="space-y-4">
              <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Roster</h2>
              {view.shooters.length === 0 ? (
                <p className="text-sm text-[var(--color-text-secondary)]">
                  No one is in Round 2 yet. Qualifying Round 1 teams and all their members are added automatically. Resolve any qualification tie on the Round 1 Teams page.
                </p>
              ) : (
                groups.map((group) => (
                  <div key={group.key}>
                    <h3 className="mb-1 text-sm font-medium text-[var(--color-text-secondary)]">{group.title}</h3>
                    <ul className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-1)]">
                      {group.rows.map((row) => (
                        <li key={row.id} className="space-y-2 border-b border-[var(--color-border)] px-3 py-3 last:border-b-0">
                          <div className="flex items-start justify-between gap-3">
                            <Link to={scoringRound2ShooterPath(slug, row)} className="min-w-0">
                              <span className="block font-medium text-[var(--color-text-primary)]">{row.name}</span>
                              <span className="text-sm text-[var(--color-text-tertiary)]">
                                {round2GenderLabel(row.gender)}
                                {row.bib ? ` · ${row.bib}` : ''}
                                {row.rank > 0 ? ` · ${row.totalDisplay}` : ''}
                              </span>
                            </Link>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))
              )}
            </section>

            <section id="round2-team-board" aria-label="Team leaderboard" className="space-y-2">
              <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Team leaderboard</h2>
              <p className="text-sm text-[var(--color-text-secondary)]">
                Ranked by the Round 2 total.
              </p>
              <ol className="space-y-3">
                {view.teamBoard.map((team) => (
                  <li key={team.teamId} className="rounded-[var(--radius-lg)] border border-[var(--color-border)] p-3">
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="text-base font-semibold text-[var(--color-text-primary)]">
                        <span className="tabular-nums text-[var(--color-text-tertiary)]">{team.rank ? `${team.rank}.` : '—'} </span>
                        {team.name}
                      </h3>
                      <span className="text-right">
                        <span className="block text-xl font-semibold tabular-nums">{team.totalDisplay}</span>
                      </span>
                    </div>
                    {team.members.length === 0 ? (
                      <p className="mt-1 text-sm text-[var(--color-text-tertiary)]">No shooters yet.</p>
                    ) : (
                      <ul className="mt-2 space-y-1 text-sm">
                        {team.members.map((member) => (
                          <li key={member.shooterId} className="flex justify-between gap-2">
                            <span className="min-w-0 truncate">
                              {member.name}
                              <span className="text-[var(--color-text-tertiary)]">
                                {' '}
                                · {round2GenderLabel(member.gender)}
                              </span>
                            </span>
                            <span className="tabular-nums">{member.totalDisplay}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ol>
            </section>
          </>
        ) : detail && rifle && !error ? (
          <p className="text-sm text-[var(--color-text-secondary)]">Loading Round 2…</p>
        ) : null}
      </div>

    </Layout>
  );
}

function groupRoster(view: Round2AdminView): Array<{ key: string; title: string; rows: Round2AdminView['shooters'] }> {
  const unassigned = view.shooters.filter((row) => !row.teamId);
  const groups: Array<{ key: string; title: string; rows: Round2AdminView['shooters'] }> = [];
  if (unassigned.length > 0) groups.push({ key: 'unassigned', title: 'Not on a team', rows: unassigned });
  for (const team of view.teams) {
    const rows = view.shooters.filter((row) => row.teamId === team.id);
    if (rows.length > 0) groups.push({ key: team.id, title: team.name, rows });
  }
  return groups;
}
